import React, { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, Gauge } from "lucide-react";

const fmtMs = (ms) => (ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`);
const fmtN = (n) => (n || 0).toLocaleString("fr-FR");
const muted = { color: "var(--yalola-text-muted)" };

const Bucket = ({ label, value, total, color }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="w-32 shrink-0" style={muted}>{label}</span>
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--yalola-surface-alt)" }}>
        <div className="h-full rounded-full transition-[width]" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-10 text-right tabular-nums font-semibold">{pct}%</span>
    </div>
  );
};

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-lg px-3 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
    <div className="text-[10px] uppercase tracking-wide" style={muted}>{label}</div>
    <div className="text-lg font-bold tabular-nums" data-testid={testid}>{value}</div>
    {sub && <div className="text-[10px] tabular-nums" style={muted}>{sub}</div>}
  </div>
);

// Lot 555 — tendance 30 jours : lectures par jour (barres) + moyenne et médiane (courbes).
const TrendChart = ({ days }) => {
  const rows = (days || []).map((d) => ({ ...d, label: d.day.slice(8, 10) + "/" + d.day.slice(5, 7) }));
  if (!rows.some((r) => r.count)) return null;
  return (
    <div data-testid="reel-speed-trend">
      <div className="text-xs font-semibold mb-1">Tendance 30 jours</div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 6, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--yalola-border)" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={4} />
            <YAxis yAxisId="ms" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${v}`)} />
            <YAxis yAxisId="n" orientation="right" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
            <ChartTooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v, name) => [name === "Lectures" ? fmtN(v) : fmtMs(v), name]} />
            <Bar yAxisId="n" dataKey="count" name="Lectures" fill="var(--yalola-surface-alt)" stroke="var(--yalola-border)" radius={[3, 3, 0, 0]} />
            <Line yAxisId="ms" type="monotone" dataKey="avg_ms" name="Moyenne" stroke="#D97757" strokeWidth={2} dot={false} connectNulls />
            <Line yAxisId="ms" type="monotone" dataKey="p50_ms" name="Médiane" stroke="#2D5A4C" strokeWidth={2} dot={false} connectNulls />
            <Line yAxisId="ms" type="monotone" dataKey="ios_avg_ms" name="iPhone" stroke="#5B7DB1" strokeWidth={1} strokeDasharray="4 3" dot={false} connectNulls />
            <Line yAxisId="ms" type="monotone" dataKey="android_avg_ms" name="Android" stroke="#7BAA97" strokeWidth={1} strokeDasharray="4 3" dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px]" style={muted}>
        Comment lire : une <b>montée progressive</b> accompagnée de plus de lectures par jour, de réseaux 3G/2G ou de pays éloignés = public plus varié (normal après la sortie publique) ; une <b>marche brutale</b> le jour d'une mise à jour = régression à corriger.
      </p>
    </div>
  );
};

const DimTable = ({ title, rows, testid, emptyHint }) => {
  const max = Math.max(1, ...(rows || []).map((r) => r.avg_ms || 0));
  return (
    <div className="rounded-lg p-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid={testid}>
      <div className="text-[11px] font-semibold mb-1.5">{title}</div>
      {!rows?.length ? (
        <div className="text-[11px]" style={muted}>{emptyHint || "Pas encore de mesures."}</div>
      ) : rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2 text-[11px] py-0.5" data-testid={`${testid}-${r.key}`}>
          <span className="w-28 sm:w-36 shrink-0 truncate" title={r.label}>{r.label}</span>
          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--yalola-surface)" }}>
            <div className="h-full rounded-full" style={{ width: `${Math.round(((r.avg_ms || 0) / max) * 100)}%`, background: (r.avg_ms || 0) >= 1000 ? "#dc2626" : (r.avg_ms || 0) >= 700 ? "#f59e0b" : "#16a34a" }} />
          </div>
          <span className="w-14 text-right tabular-nums font-semibold">{fmtMs(r.avg_ms)}</span>
          <span className="w-12 text-right tabular-nums" style={muted}>{fmtN(r.count)}</span>
        </div>
      ))}
    </div>
  );
};

const NEW_APP_HINT = "Mesuré à partir de la mise à jour 2.0.4 (21) / 1.5.9 (20) de l'app et de la version web qui l'accompagne.";

export const ReelSpeedStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reels/startup-stats");
      setStats(data);
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const p = stats?.last7 || {};
  const p30 = stats?.last30 || {};
  const dims = p.dims || {};
  const total = p.count || 0;
  return (
    <div className="mt-4 rounded-xl border p-4 space-y-3" style={{ borderColor: "var(--yalola-border)" }} data-testid="reel-speed-stats">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Gauge size={16} />
          <div>
            <div className="text-sm font-semibold">Vitesse de démarrage réelle des Reels</div>
            <p className="text-[11px]" style={muted}>
              Temps mesuré chez vos membres entre l'arrivée sur un Reel et sa première image (7 derniers jours). Ce trajet ne passe pas par le serveur Yalola : il dépend du téléphone, du réseau et du CDN vidéo.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={load} disabled={loading} data-testid="reel-speed-refresh">
          <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />Actualiser
        </Button>
      </div>
      {!total ? (
        <p className="text-xs" style={muted} data-testid="reel-speed-empty">
          Pas encore de mesures — elles apparaîtront dès que des membres regarderont des Reels.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <Stat label="Moyenne 7 j" value={fmtMs(p.avg_ms)} sub={`30 j : ${fmtMs(p30.avg_ms)}`} testid="reel-speed-avg7" />
            <Stat label="Médiane 7 j" value={fmtMs(p.p50_ms)} sub={p.p50_ms == null ? NEW_APP_HINT : `30 j : ${fmtMs(p30.p50_ms)}`} testid="reel-speed-p50" />
            <Stat label="p90 7 j" value={fmtMs(p.p90_ms)} sub={p.p90_ms == null ? "9 lectures sur 10 démarrent plus vite" : `30 j : ${fmtMs(p30.p90_ms)}`} testid="reel-speed-p90" />
            <Stat label="Aujourd'hui" value={fmtMs(stats?.today?.avg_ms)} sub={`${fmtN(stats?.today?.count)} lectures`} testid="reel-speed-avg-today" />
            <Stat label="Reels HLS (léger)" value={fmtMs(p.hls_avg_ms)} sub={`${fmtN(p.hls_count)} lectures`} testid="reel-speed-avg-hls" />
            <Stat label="Reels MP4 (lourd)" value={fmtMs(p.mp4_avg_ms)} sub={`${fmtN(p.mp4_count)} lectures`} testid="reel-speed-avg-mp4" />
          </div>
          {(p.ios_count || p.android_count || p.web_count) ? (
            <div className="grid grid-cols-3 gap-2" data-testid="reel-speed-platforms">
              <Stat label={`iPhone · ${fmtN(p.ios_count)}`} value={fmtMs(p.ios_avg_ms)} testid="reel-speed-avg-ios" />
              <Stat label={`Android · ${fmtN(p.android_count)}`} value={fmtMs(p.android_avg_ms)} testid="reel-speed-avg-android" />
              <Stat label={`Web · ${fmtN(p.web_count)}`} value={fmtMs(p.web_avg_ms)} testid="reel-speed-avg-web" />
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Bucket label="Instantané (< 0,3 s)" value={p.b_instant || 0} total={total} color="#16a34a" />
            <Bucket label="Rapide (0,3 – 0,7 s)" value={p.b_fast || 0} total={total} color="#84cc16" />
            <Bucket label="Lent (0,7 – 1,5 s)" value={p.b_slow || 0} total={total} color="#f59e0b" />
            <Bucket label="Très lent (> 1,5 s)" value={p.b_very_slow || 0} total={total} color="#dc2626" />
          </div>
          <TrendChart days={stats?.days} />
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3" data-testid="reel-speed-dims">
            <DimTable title="Réseau du membre" rows={dims.net} testid="reel-speed-dim-net" emptyHint={NEW_APP_HINT} />
            <DimTable title="Première lecture vs déjà vu" rows={dims.cold} testid="reel-speed-dim-cold" emptyHint={NEW_APP_HINT} />
            <DimTable title="Préchargement" rows={dims.pre} testid="reel-speed-dim-pre" emptyHint={NEW_APP_HINT} />
            <DimTable title="Pays du membre" rows={dims.country} testid="reel-speed-dim-country" emptyHint={NEW_APP_HINT} />
            <DimTable title="Version du système" rows={dims.osv} testid="reel-speed-dim-osv" emptyHint={NEW_APP_HINT} />
            <DimTable title="Lecteur vidéo" rows={dims.player} testid="reel-speed-dim-player" emptyHint={NEW_APP_HINT} />
          </div>
          <p className="text-[11px]" style={muted}>
            {fmtN(total)} lectures mesurées sur 7 jours · moyenne 30 j : {fmtMs(p30.avg_ms)} · chaque ligne : temps moyen puis nombre de lectures.
          </p>
        </>
      )}
    </div>
  );
};
